<template>
  <div>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 v-if="isParkAdmin">Solicitar Empresa de Seguridad</h1>
          <h1 v-else>{{ $t('security_link_company') }}</h1>
          <p v-if="isParkAdmin">Selecciona una empresa de seguridad para solicitar sus servicios de vigilancia.</p>
          <p v-else>{{ $t('security_link_company_description') }}</p>
        </v-flex>
      </v-layout>

      <!-- Park Admin: Solicitudes de Empresas de Seguridad -->
      <v-layout row wrap v-if="isParkAdmin && (parkContracts.active.length > 0 || parkContracts.pending.length > 0 || parkContracts.rejected.length > 0)" class="mt-4">
        <v-flex xs12>
          <h2 class="primary--text mb-4">Mis Contratos de Seguridad</h2>
        </v-flex>

        <!-- Contratos Activos -->
        <v-flex xs12 sm6 md4 v-for="contract in parkContracts.active" :key="`active-${contract.id}`" class="pa-2">
          <v-card class="elevation-3 rounded-lg" style="border-left: 4px solid #4CAF50;">
            <v-card-text class="pa-4">
              <div class="mb-2 d-flex align-center justify-space-between">
                <h3 class="subheading font-weight-bold mb-0">{{ contract.security_company_name }}</h3>
              </div>
              <v-chip color="success" text-color="white" small class="mb-3 ml-0">
                <v-icon left small>check_circle</v-icon>
                Activo
              </v-chip>
              <div>
                <small class="grey--text text-uppercase">Contratado desde</small>
                <p class="body-2 font-weight-bold mb-0">{{ formatDate(contract.created_at) }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Contratos Pendientes -->
        <v-flex xs12 sm6 md4 v-for="contract in parkContracts.pending" :key="`pending-${contract.id}`" class="pa-2">
          <v-card class="elevation-3 rounded-lg" style="border-left: 4px solid #FFC107;">
            <v-card-text class="pa-4">
              <div class="mb-2 d-flex align-center justify-space-between">
                <h3 class="subheading font-weight-bold mb-0">{{ contract.security_company_name }}</h3>
              </div>
              <v-chip color="warning" text-color="white" small class="mb-3 ml-0">
                <v-icon left small>schedule</v-icon>
                Pendiente
              </v-chip>
              <div class="mb-3">
                <small class="grey--text text-uppercase">Solicitado</small>
                <p class="body-2 font-weight-bold mb-0">{{ formatDate(contract.created_at) }}</p>
              </div>
              <v-alert type="info" :value="true" dense class="mb-0 caption">
                Esperando respuesta de la empresa
              </v-alert>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Contratos Rechazados -->
        <v-flex xs12 sm6 md4 v-for="contract in parkContracts.rejected" :key="`rejected-${contract.id}`" class="pa-2">
          <v-card class="elevation-3 rounded-lg" style="border-left: 4px solid #F44336;">
            <v-card-text class="pa-4">
              <div class="mb-2 d-flex align-center justify-space-between">
                <h3 class="subheading font-weight-bold mb-0">{{ contract.security_company_name }}</h3>
              </div>
              <v-chip color="error" text-color="white" small class="mb-3 ml-0">
                <v-icon left small>cancel</v-icon>
                Rechazado
              </v-chip>
              <div>
                <small class="grey--text text-uppercase">Solicitado</small>
                <p class="body-2 font-weight-bold mb-0">{{ formatDate(contract.created_at) }}</p>
              </div>
              <small class="grey--text">Puedes intentar con otra empresa</small>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Client Admin: Current Contract Info -->
      <v-layout row wrap v-else-if="!isParkAdmin && hasAnyContract">
        <v-flex xs12>
          <v-card class="mb-3">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ $t('security_active_contract') }}</h3>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('company_name') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ (activeContract.companySecurityProvider && typeof activeContract.companySecurityProvider === 'object' ? activeContract.companySecurityProvider.nombre : activeContract.security_name) || 'N/A' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('status') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-chip :color="getStatusColor(activeContract.status)" text-color="white" small>
                        {{ activeContract.status_display || activeContract.status }}
                      </v-chip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile v-if="activeContract.companySecurityProvider && typeof activeContract.companySecurityProvider === 'object'">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('contact_email') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ activeContract.companySecurityProvider.email || 'N/A' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile v-if="activeContract.companySecurityProvider && typeof activeContract.companySecurityProvider === 'object'">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('telephone') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ activeContract.companySecurityProvider.telephone || 'N/A' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <v-alert v-if="activeContract.status === 'PENDING'" type="warning" class="mt-3">
                {{ $t('status') }}: Esperando aprobación de la empresa de seguridad
              </v-alert>
              <v-btn color="error" @click="showDeleteDialog = true" class="mt-3">
                {{ $t('security_unlink_company') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Available Companies List -->
      <v-layout row wrap v-if="isParkAdmin ? !parkHasOngoingContract : !hasAnyContract" class="mt-4">
        <v-flex xs12>
          <h2 class="primary--text mb-4">{{ isParkAdmin ? 'Solicitar Empresa de Seguridad' : 'Empresas de Seguridad Disponibles' }}</h2>
        </v-flex>

        <v-flex v-if="isSecurityLoading" xs12>
          <v-progress-linear indeterminate></v-progress-linear>
          <p class="mt-3 text-center grey--text">Cargando empresas...</p>
        </v-flex>

        <v-flex v-else-if="getSecurityError" xs12>
          <v-alert type="error" dismissible>
            {{ getSecurityError }}
          </v-alert>
        </v-flex>

        <v-flex v-else-if="availableCompanies.length > 0" xs12>
          <v-layout row wrap>
            <v-flex xs12 sm6 md4 v-for="company in availableCompanies" :key="company.id" class="pa-2 d-flex">
              <v-card class="elevation-2 rounded-lg company-card d-flex flex-column">
                <v-card-text class="pa-4 flex-grow-1">
                  <div class="d-flex align-center mb-3">
                    <v-avatar color="primary lighten-4" size="48" class="mr-3">
                      <v-icon color="primary" size="26">security</v-icon>
                    </v-avatar>
                    <div class="text-truncate">
                      <h3 class="subheading font-weight-bold mb-0 text-truncate">
                        {{ (company.companySecurityProvider && company.companySecurityProvider.nombre) || company.name || 'N/A' }}
                      </h3>
                      <small class="grey--text">Empresa de Seguridad</small>
                    </div>
                  </div>

                  <v-divider class="mb-3"></v-divider>

                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-2">email</v-icon>
                    <span class="body-2 grey--text text--darken-2 text-truncate">
                      {{ (company.companySecurityProvider && company.companySecurityProvider.email) || company.email || 'Sin email registrado' }}
                    </span>
                  </div>
                </v-card-text>

                <v-card-actions class="pa-3 pt-0">
                  <v-btn
                    color="primary"
                    depressed
                    block
                    @click="selectCompany(company)"
                    :loading="linkingCompanyId === company.id"
                  >
                    <v-icon left small>{{ isParkAdmin ? 'send' : 'link' }}</v-icon>
                    <span v-if="isParkAdmin">Solicitar</span>
                    <span v-else>Seleccionar</span>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-else xs12>
          <v-alert type="info">
            {{ $t('security_no_available_companies') }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="showConfirmDialog" max-width="400px">
        <v-card>
          <v-card-title v-if="isParkAdmin">Confirmar Solicitud</v-card-title>
          <v-card-title v-else>{{ $t('security_confirm_link') }}</v-card-title>
          <v-card-text>
            <p v-if="selectedCompany && isParkAdmin">
              ¿Solicitar servicios de seguridad a
              <strong>{{ (selectedCompany.companySecurityProvider && selectedCompany.companySecurityProvider.nombre) || selectedCompany.name || 'N/A' }}</strong>?
              Esta empresa recibirá tu solicitud y podrá aceptarla o rechazarla.
            </p>
            <p v-else-if="selectedCompany">
              {{ $t('confirm_link_msg') }}
              <strong>{{ (selectedCompany.companySecurityProvider && selectedCompany.companySecurityProvider.nombre) || selectedCompany.name || 'N/A' }}</strong>?
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showConfirmDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="primary" flat @click="linkCompany" :loading="isSecurityLoading">
              <span v-if="isParkAdmin">Enviar Solicitud</span>
              <span v-else>{{ $t('confirm') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="400px">
        <v-card>
          <v-card-title v-if="isParkAdmin">Cancelar Solicitud</v-card-title>
          <v-card-title v-else>{{ $t('security_confirm_unlink') }}</v-card-title>
          <v-card-text>
            <p v-if="isParkAdmin">¿Estás seguro de que deseas cancelar la solicitud a esta empresa de seguridad?</p>
            <p v-else>{{ $t('confirm_unlink_msg') }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showDeleteDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="error" flat @click="unlinkCompany" :loading="isSecurityLoading">
              {{ $t('unlink') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { FETCH_AVAILABLE_COMPANIES, LINK_COMPANY, DELETE_LINK, FETCH_CONTRACT } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'
import axios from 'axios'

export default {
  name: 'LinkSecurityCompany',
  data() {
    return {
      showConfirmDialog: false,
      showDeleteDialog: false,
      selectedCompany: null,
      linkingCompanyId: null,
      userRole: null,
      parkContracts: {
        pending: [],
        active: [],
        rejected: []
      }
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'getAvailableCompanies',
      'isSecurityLoading',
      'getSecurityError',
      'getContract',
      'hasAnyContract'
    ]),
    isParkAdmin() {
      return this.userRole === 4
    },
    isClientAdmin() {
      return this.userRole === 5
    },
    availableCompanies() {
      return this.getAvailableCompanies
    },
    activeContract() {
      return this.getContract
    },
    // Un parque con contrato ACTIVE o PENDING ya no debe poder enviar otra
    // solicitud (evita solicitudes duplicadas mientras una está en curso).
    parkHasOngoingContract() {
      return this.parkContracts.active.length > 0 || this.parkContracts.pending.length > 0
    }
  },
  methods: {
    selectCompany(company) {
      this.selectedCompany = company
      this.showConfirmDialog = true
    },
    getStatusColor(status) {
      const colors = {
        'ACTIVE': 'green',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return colors[status] || 'grey'
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    linkCompany() {
      this.linkingCompanyId = this.selectedCompany.id

      // Para Parque Admin: enviar solicitud a empresa de seguridad
      if (this.isParkAdmin) {
        this.linkParkToSecurityCompany()
      } else {
        // Para Cliente: vincular empresa de seguridad
        this.linkClientToSecurityCompany()
      }
    },
    linkClientToSecurityCompany() {
      this.$store.dispatch(`security/${LINK_COMPANY}`, {
        token: this.getToken,
        securityCompanyId: this.selectedCompany.id
      })
      .then(() => {
        this.showConfirmDialog = false
        this.$store.commit('snackbar_change_message', this.$t('security_company_linked_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.$router.push('/security/view-contract')
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_linking_company'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.linkingCompanyId = null
      })
    },
    linkParkToSecurityCompany() {
      const API_BASE_URL = 'https://empresa-service.intrare.app'

      const config = {
        method: 'post',
        url: `${API_BASE_URL}/security/link-park/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        },
        data: {
          security_company_id: this.selectedCompany.id
        }
      }

      axios(config)
        .then(resp => {
          this.showConfirmDialog = false
          this.$store.commit('snackbar_change_message', 'Solicitud enviada a ' + (this.selectedCompany.nombre || this.selectedCompany.name || 'la empresa'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.fetchData()
        })
        .catch(err => {
          this.$store.commit('snackbar_change_message', 'Error al enviar solicitud a empresa de seguridad')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
        .finally(() => {
          this.linkingCompanyId = null
        })
    },
    unlinkCompany() {
      this.$store.dispatch(`security/${DELETE_LINK}`, this.getToken)
      .then(() => {
        this.showDeleteDialog = false
        this.$store.commit('snackbar_change_message', this.$t('security_company_unlinked_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_unlinking_company'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    fetchData() {
      this.$store.dispatch(`security/${FETCH_AVAILABLE_COMPANIES}`, this.getToken)

      // Parque Admin: usar endpoint de contratos del parque
      if (this.isParkAdmin) {
        this.fetchParkSecurityContracts()
      } else {
        // Cliente: usar endpoint de contrato de empresa
        this.$store.dispatch(`security/${FETCH_CONTRACT}`, this.getToken)
      }
    },
    fetchParkSecurityContracts() {
      const API_BASE_URL = 'https://empresa-service.intrare.app'
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/security/my-security-contracts/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        }
      }

      axios(config)
        .then(resp => {
          // Guardar contratos agrupados por estado
          const contractData = resp.data || {}
          this.parkContracts = contractData.contracts || {
            pending: [],
            active: [],
            rejected: []
          }

          // También guardar en el store para compatibilidad
          const activeContract = (this.parkContracts.active && this.parkContracts.active[0]) || null
          this.$store.commit('security/SET_CONTRACT', activeContract)
        })
        .catch(err => {
          console.error('Error fetching park security contracts:', err)
          this.parkContracts = { pending: [], active: [], rejected: [] }
          this.$store.commit('security/SET_CONTRACT', null)
        })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    // Detectar rol del usuario
    const profile = this.getProfile
    if (profile && profile.roll) {
      this.userRole = profile.roll
    }

    // Esperar a que el perfil esté cargado antes de cargar datos
    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          const updatedProfile = this.$store.getters.getProfile
          if (updatedProfile && updatedProfile.roll) {
            this.userRole = updatedProfile.roll
          }
          this.fetchData()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}
.mt-3 {
  margin-top: 20px;
}
.company-card {
  width: 100%;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.company-card:hover {
  transform: translateY(-2px);
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
